import DOMPurify from "dompurify";

// Fixed, one-time-authored opening line - per direct instruction, this is
// NOT a per-quotation editable field (no DB column, no rich-text editor);
// if the wording ever needs to change, it's a code change here, not a
// data-entry field. The greeting itself ("Dear {customer},") is rendered
// separately by StockDocumentPreview.jsx using the real customer name -
// this string is only the body that follows it.
const COVER_LETTER_TEXT =
  "Thank you for reaching out to us. Following the security assessment of your premises, please find the cost estimate for the requested security personnel / equipment(s) below.";

// Mirrors stock/lpo/buildLpoPrintProps.js exactly - the same reusable
// StockDocumentPreview.jsx template (branch letterhead, party block, line-
// item table, totals, Prepared/Approved By + signature) now also serves
// Quotation printing, per direct instruction, instead of a one-off second
// template. signatureImageUrl is a data: URI (see blobToDataUrl in
// buildLpoPrintProps.js, reused as-is for Quotation too). serviceTypes is
// the quotation's own branch's Service Type master list (caller fetches
// via useGetServiceTypesQuery({branchId, isActive:true}) - this function
// stays a plain synchronous mapper, no data fetching of its own).
// enquiryServiceTypes is the linked Sales Enquiry's own selected service
// types (caller fetches via useGetEnquiryQuery(salesEnquiryRecordGuid) when
// the quotation has one) - only used for the single heading line below.
const netOfDiscount = (q) => (Number(q.subTotal) || 0) - (Number(q.discountAmount) || 0);

export const buildQuotationPrintProps = (quotation, companyInfo, signatureImageUrl, serviceTypes = [], enquiryServiceTypes = []) => {
  const companyName = companyInfo?.["Company.Name"] || "Texas Alarms";
  // Single centered/underlined heading under the yellow banner. When the
  // quotation is linked to a Sales Enquiry, name the services that enquiry
  // was actually raised for ("QUOTATION FOR FIRE ALARM, CCTV"). Otherwise
  // (a from-scratch quotation, no enquiry link) fall back to a plain
  // "Quotation from {company}" line.
  //
  // 2026-09-19 (reverses the earlier "don't use the prototype name"): a
  // quotation built from a prototype is headed "QUOTATION FOR {prototype}",
  // and that wins over the enquiry's service types - the prototype names the
  // exact system being quoted ("Electric Fence"), the enquiry only the areas
  // of interest.
  const prototypeName = (quotation.prototypeName || "").trim();
  const quotationSubject = prototypeName
    ? `QUOTATION FOR ${prototypeName.toUpperCase()}`
    : enquiryServiceTypes.length > 0
    ? `QUOTATION FOR ${enquiryServiceTypes.map((s) => s.serviceTypeName).join(", ").toUpperCase()}`
    : `Quotation from ${companyName}`;

  const printLines = quotation.lines.map((l) => ({
    qty: l.qty,
    productName: l.description,
    price: l.rate,
    vatAmount: (Number(l.qty) || 0) * (Number(l.rate) || 0) * ((Number(l.vatPercent) || 0) / 100),
    amount: (Number(l.qty) || 0) * (Number(l.rate) || 0),
  }));

  return {
    title: "QUOTATION",
    docNo: quotation.quotationNumber,
    docDate: new Date(quotation.quotationDate).toLocaleDateString(),
    // The linked Sales Enquiry/Order number is internal provenance (shown
    // elsewhere in the app's own Quotation Detail page) - not meant for a
    // customer-facing "Reference No" line, per direct instruction.
    refNo: "",
    branchLabel: quotation.branchCode,
    // 4-5 of the branch's own configured Service Type names, joined onto
    // one line for the yellow letterhead banner - real master data instead
    // of a hardcoded tagline, per direct instruction ("we use the service
    // type master items as text").
    letterheadBannerText: serviceTypes.slice(0, 5).map((s) => s.serviceTypeName).join(", "),
    // Single centered/underlined heading, rendered by StockDocumentPreview.jsx
    // right under the yellow banner (see quotationSubject computation above).
    quotationSubject,
    partyLabel: "Customer",
    partyName: quotation.customerName,
    partyAddress: quotation.customerAddress || "",
    partyMobile: quotation.customerPhone || "",
    // The header block's own small Remarks slot is left blank for
    // Quotation - the rich "Footer Notes" content goes in the dedicated
    // footerNotesHtml block after the line items instead (see below),
    // not squeezed into this narrow party-info cell.
    remarks: "",
    showVat: true,
    // VAT is charged on the NET (sub total less any discount, Batch163), so the
    // printed rate is worked out against the net - against the gross it would
    // print 14% for a 16% quotation carrying a 10% discount.
    vatPercent: quotation.vatAmount && netOfDiscount(quotation) ? Math.round((quotation.vatAmount / netOfDiscount(quotation)) * 100) : 0,
    lines: printLines,
    totals: {
      subTotal: quotation.subTotal,
      vatAmount: quotation.vatAmount,
      netAmount: quotation.totalAmount,
      discountAmount: quotation.discountAmount || 0,
      discountLabel: quotation.discountPercent ? `Discount (${Number(quotation.discountPercent)}%)` : "Discount",
    },
    preparedByName: quotation.createdByName,
    approvedByName: quotation.approvedByName,
    approvedByDesignation: quotation.approvedByDesignation,
    approvedDate: quotation.approvedDate ? new Date(quotation.approvedDate).toLocaleString() : null,
    signatureImageUrl: signatureImageUrl || undefined,
    coverLetterHtml: COVER_LETTER_TEXT,
    // Sanitized once here (not at every render site) - the same docProps
    // object feeds both the live preview and the print popup's
    // ReactDOMServer.renderToStaticMarkup pass.
    footerNotesHtml: quotation.remarks ? DOMPurify.sanitize(quotation.remarks) : undefined,
    ...(companyInfo && {
      companyName: companyInfo["Company.Name"] || undefined,
      companyAddress: companyInfo["Company.Address"] || undefined,
      companyPhone: companyInfo["Company.Phone"] || undefined,
      companyEmail: companyInfo["Company.Email"] || undefined,
    }),
  };
};

// A Quotation is only meaningful to hand to a customer once approved -
// SUBMITTED/REJECTED/CANCELLED have no business being printed/sent.
export const QUOTATION_PRINTABLE_STATUSES = ["APPROVED"];
